import { useState } from 'react';
import { ColorPickerPopover } from '@components/floorplan-v2/color-picker-popover';
import { IconPencil } from '@components/icons';
import { LabelPrimary } from '@components/label';
import classNames from 'classnames';

import { ColorSwatchSelectorProps } from './color-swatch-selector.type';

const DEFAULT_MAX_COLORS = 10;

const normalise = (color: string) => color.toLowerCase();

const dedup = (colors: string[]): string[] => Array.from(new Set(colors.map(normalise)));

export const ColorSwatchSelector = ({
  value,
  onChange,
  label,
  initialColors = [],
  onColorsChange,
  floorplanImageUrl,
  maxColors = DEFAULT_MAX_COLORS,
  zIndex,
  editable = false,
}: ColorSwatchSelectorProps) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const persistedColors = dedup(initialColors).slice(0, maxColors);
  const normalisedValue = normalise(value);
  const isCurrentPersisted = persistedColors.includes(normalisedValue);
  // An empty value means "no colour": render no current swatch (it'd be a blank/black circle) — the
  // picker's "+" trigger is the only affordance until a colour is picked again.
  const hasCurrentColor = normalisedValue !== '';

  const handlePickerChange = (hex: string) => {
    const normalised = normalise(hex);
    onChange(normalised);
  };

  const handleSwatchClick = (color: string) => {
    onChange(color);
    if (!persistedColors.includes(color)) {
      const updated = [...persistedColors, color];
      onColorsChange?.(updated);
    }
  };

  const handlePickFromFloorplan = (hex: string) => {
    const normalised = normalise(hex);
    onChange(normalised);
    if (!persistedColors.includes(normalised)) {
      const updated = [...persistedColors, normalised].slice(-maxColors);
      onColorsChange?.(updated);
    }
  };

  // Editable mode: a second click on the selected pill opens the picker; floorplan keeps single-click select.
  const handlePillClick = (color: string) => {
    if (editable && normalisedValue === color) {
      setIsPickerOpen(true);
      return;
    }
    handleSwatchClick(color);
  };

  const handleDelete = () => {
    onChange('');
    setIsPickerOpen(false);
  };

  const handleDone = () => setIsPickerOpen(false);

  const renderEditBadge = (color: string) =>
    editable && normalisedValue === color ? (
      <span
        aria-hidden='true'
        className='absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary shadow-[0_0_0_1px_rgba(0,0,0,0.15)]'>
        <IconPencil className='h-3 w-3 text-primary' />
      </span>
    ) : null;

  return (
    <div>
      {label && <LabelPrimary>{label}</LabelPrimary>}
      <div className='flex flex-wrap items-center gap-2'>
        {persistedColors.map((color) => (
          <button
            key={color}
            type='button'
            aria-label={color}
            aria-pressed={normalisedValue === color}
            onClick={() => handlePillClick(color)}
            className={classNames(
              'relative w-8 h-8 rounded-full border-2 transition-all focus:outline-none focus:ring-2 focus:ring-offset-1',
              {
                'border-medium scale-110': normalisedValue === color,
                'border-transparent': normalisedValue !== color,
              }
            )}
            style={{ backgroundColor: color }}>
            {renderEditBadge(color)}
          </button>
        ))}

        {/* Current color swatch — only shown when set and not already in the persisted list */}
        {hasCurrentColor && !isCurrentPersisted && (
          <button
            key={normalisedValue}
            type='button'
            aria-label={normalisedValue}
            aria-pressed={true}
            className='relative w-8 h-8 rounded-full border-2 border-medium scale-110 transition-all focus:outline-none focus:ring-2 focus:ring-offset-1'
            style={{ backgroundColor: normalisedValue }}
            onClick={() => handlePillClick(normalisedValue)}>
            {renderEditBadge(normalisedValue)}
          </button>
        )}

        <ColorPickerPopover
          color={value}
          onChange={handlePickerChange}
          onPickFromFloorPlan={handlePickFromFloorplan}
          floorplanImageUrl={floorplanImageUrl}
          placement='top'
          zIndex={zIndex}
          isOpen={editable ? isPickerOpen : undefined}
          onOpenChange={editable ? setIsPickerOpen : undefined}
          onDelete={editable ? handleDelete : undefined}
          onDone={editable ? handleDone : undefined}
        />
      </div>
    </div>
  );
};
